import React from "react";
import { ASPECTS, getFrame, type AspectId } from "./frame";
import { THEMES, getTheme } from "./themes";
import { SCENES } from "./scenes";
import { Stage } from "./Stage";
import { Storyboard } from "./Storyboard";
import "./ui.css";

/**
 * Anivaze v4 — Phase 1 + storyboard preview harness.
 *
 * Two modes share one set of design controls (theme / aspect / guides):
 *  • "Scenes" — hand-authored scenes proving reflow + re-theme (Phase 1).
 *  • "From script" — paste a script → deterministic, editable storyboard that
 *    plays (the anti-gimmick flow, PLAN §20–21).
 */
type Tab = "scenes" | "script";

export const Phase1Preview: React.FC<{ onExit?: () => void }> = ({ onExit }) => {
  const [tab, setTab] = React.useState<Tab>("script");
  const [aspect, setAspect] = React.useState<AspectId>("9:16");
  const [themeId, setThemeId] = React.useState("bold");
  const [guides, setGuides] = React.useState(false);

  const theme = getTheme(themeId);

  return (
    <div style={S.root}>
      <header style={S.header}>
        <div style={S.brand}>
          <span style={S.dot} />
          Anivaze <span style={S.muted}>· learning-video maker</span>
        </div>
        <button type="button" className="v4-btn" style={S.exit} onClick={() => (onExit ? onExit() : (window.location.hash = "legacy"))}>
          Previous version →
        </button>
      </header>

      <div style={S.tabs}>
        {(["script", "scenes"] as Tab[]).map((tb) => (
          <button key={tb} className="v4-pill" aria-pressed={tab === tb} onClick={() => setTab(tb)} style={{ ...S.tab, ...(tab === tb ? S.tabOn : null) }}>
            {tb === "script" ? "From script" : "Demo scenes"}
          </button>
        ))}
      </div>

      <div style={S.body}>
        <div style={S.main}>
          {tab === "script" ? (
            <Storyboard theme={theme} aspect={aspect} guides={guides} />
          ) : (
            <DemoPlayer theme={theme} aspect={aspect} guides={guides} />
          )}
        </div>

        <aside style={S.panel}>
          <Section label="Theme — switch free, instantly">
            {THEMES.map((th) => (
              <Pill key={th.id} active={th.id === themeId} onClick={() => setThemeId(th.id)} swatch={th.palette.primary}>
                {th.label}
              </Pill>
            ))}
          </Section>
          <Section label="Aspect ratio — switch free, instantly">
            {ASPECTS.map((a) => (
              <Pill key={a} active={a === aspect} onClick={() => setAspect(a)}>
                {a}
              </Pill>
            ))}
          </Section>
          <Section label="Overlay">
            <Pill active={guides} onClick={() => setGuides((g) => !g)}>
              {guides ? "Safe-area guides: on" : "Safe-area guides: off"}
            </Pill>
          </Section>
          <p style={S.note}>
            <b>You review and can change every beat</b> before it&rsquo;s final. Switching theme or aspect updates the
            video instantly and costs nothing — and the same beats always produce the same video.
          </p>
        </aside>
      </div>
    </div>
  );
};

// ── Demo scene player (Phase 1 reflow/re-theme proof) ─────────────────────
const DemoPlayer: React.FC<{ theme: ReturnType<typeof getTheme>; aspect: AspectId; guides: boolean }> = ({
  theme, aspect, guides,
}) => {
  const [sceneId, setSceneId] = React.useState(SCENES[0].id);
  const [playing, setPlaying] = React.useState(true);
  const [t, setT] = React.useState(0);
  const scene = SCENES.find((s) => s.id === sceneId) ?? SCENES[0];
  const frame = getFrame(aspect);

  React.useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      setT((p) => (p + dt >= scene.dur ? (p + dt) % scene.dur : p + dt));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, scene.dur]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, alignItems: "center", width: "100%" }}>
      <div style={{ display: "flex", gap: 8, alignSelf: "flex-start" }}>
        {SCENES.map((s) => (
          <Pill key={s.id} active={s.id === sceneId} onClick={() => setSceneId(s.id)}>
            {s.label}
          </Pill>
        ))}
      </div>
      <div style={{ ...S.stageBox, aspectRatio: `${frame.w} / ${frame.h}` }}>
        <Stage aspect={aspect} theme={theme} scene={scene} t={t} showGuides={guides} />
      </div>
      <div style={S.transport}>
        <button className="v4-btn-primary" aria-label={playing ? "Pause" : "Play"} title={playing ? "Pause" : "Play"} onClick={() => setPlaying((p) => !p)} style={S.play}>
          {playing ? "❚❚" : "►"}
        </button>
        <input
          type="range" min={0} max={scene.dur} step={0.01} value={t}
          onChange={(e) => { setPlaying(false); setT(parseFloat(e.target.value)); }}
          style={S.scrub} aria-label="Scrub timeline"
        />
        <span style={S.time}>{t.toFixed(1)}s / {scene.dur.toFixed(1)}s</span>
      </div>
    </div>
  );
};

const Pill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode; swatch?: string }> = ({
  active, onClick, children, swatch,
}) => (
  <button type="button" className="v4-pill" aria-pressed={active} onClick={onClick} style={{ ...S.pill, ...(active ? S.pillOn : null) }}>
    {swatch && <span style={{ ...S.swatch, background: swatch }} />}
    {children}
  </button>
);

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <div style={S.secLabel}>{label}</div>
    <div style={S.pillRow}>{children}</div>
  </div>
);

const S: Record<string, React.CSSProperties> = {
  root: { position: "fixed", inset: 0, background: "#0c0e11", color: "#e8eaed", fontFamily: "Inter, system-ui, sans-serif", display: "flex", flexDirection: "column", overflow: "auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid #1e232a", flex: "0 0 auto" },
  brand: { display: "flex", alignItems: "center", gap: 10, fontWeight: 700 },
  dot: { width: 9, height: 9, borderRadius: 999, background: "#ff5d33", boxShadow: "0 0 0 4px rgba(255,93,51,0.18)" },
  muted: { color: "#7c8794", fontWeight: 500 },
  tag: { marginLeft: 8, fontFamily: "monospace", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "#9aa4b0", border: "1px solid #262c34", borderRadius: 999, padding: "3px 9px" },
  exit: { background: "transparent", color: "#c3cad2", border: "1px solid #2a313a", borderRadius: 8, padding: "7px 12px", cursor: "pointer", fontSize: 14, minHeight: 40 },
  tabs: { display: "flex", gap: 8, padding: "12px 20px 0" },
  tab: { background: "transparent", color: "#9aa4b0", border: "1px solid #262c34", borderRadius: 999, padding: "7px 16px", cursor: "pointer", fontSize: 14, minHeight: 40 },
  tabOn: { background: "#171b20", color: "#fff", borderColor: "#3a424c" },
  body: { flex: "1 1 auto", display: "flex", gap: 20, padding: 20, minHeight: 0, flexWrap: "wrap", alignItems: "flex-start" },
  main: { flex: "1 1 420px", minWidth: 0, display: "flex" },
  stageBox: { height: "min(60vh, 640px)", maxWidth: "100%", background: "#000", borderRadius: 18, overflow: "hidden", border: "1px solid #333b44", boxShadow: "0 24px 60px -30px rgba(0,0,0,0.9)" },
  transport: { display: "flex", alignItems: "center", gap: 10, width: "min(100%, 420px)" },
  play: { width: 44, height: 44, borderRadius: 999, border: "none", background: "#ff5d33", color: "#12151a", cursor: "pointer", flex: "0 0 auto" },
  scrub: { flex: "1 1 auto", accentColor: "#ff5d33" },
  time: { fontFamily: "monospace", fontSize: 12, color: "#8a939e", fontVariantNumeric: "tabular-nums" },
  panel: { flex: "0 0 280px", display: "flex", flexDirection: "column", gap: 20, padding: 18, background: "#111418", border: "1px solid #1e232a", borderRadius: 16, maxWidth: "100%" },
  secLabel: { fontFamily: "monospace", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c8794" },
  pillRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  pill: { display: "inline-flex", alignItems: "center", gap: 7, background: "#171b20", color: "#c8cfd7", border: "1px solid #262c34", borderRadius: 999, padding: "8px 13px", cursor: "pointer", fontSize: 13 },
  pillOn: { background: "#ff5d33", color: "#12151a", borderColor: "#ff5d33", fontWeight: 600 },
  swatch: { width: 11, height: 11, borderRadius: 3, display: "inline-block", border: "1px solid rgba(0,0,0,0.25)" },
  note: { fontSize: 12.5, lineHeight: 1.5, color: "#8b949f", margin: 0, borderTop: "1px solid #1e232a", paddingTop: 16 },
};
